import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  LogOut,
  Loader2,
  FileText,
  DollarSign,
  Download,
  Send,
  CheckCircle2,
  Clock,
  Plus,
  Trash2,
  Sparkles,
  ExternalLink,
  Inbox,
  Mail,
  RefreshCw,
  Eye,
} from "lucide-react";
import { StatCard } from "@/pages/AdminDashboard";
import ComposeEmailModal from "@/components/admin/ComposeEmailModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  draft: { label: "Draft", className: "bg-slate-100 text-slate-700 ring-1 ring-slate-200" },
  sent: { label: "Sent", className: "bg-blue-100 text-blue-800 ring-1 ring-blue-200" },
  paid: { label: "Paid", className: "bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200" },
};

const FILTERS = [
  { key: "all", label: "All" },
  { key: "unpaid", label: "Unpaid" },
  { key: "paid", label: "Paid" },
];

function startOfMonth(d = new Date()) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function fmtMoney(n) {
  return `$${Number(n || 0).toFixed(2)}`;
}

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

export default function AdminInvoices() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [authState, setAuthState] = useState(
    location.state?.user ? "authed" : "checking"
  );
  const [authError, setAuthError] = useState(null);

  const [invoices, setInvoices] = useState([]);
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [busyId, setBusyId] = useState(null);
  const [composeInv, setComposeInv] = useState(null);
  const [expandedEmailsFor, setExpandedEmailsFor] = useState(null);

  const openCompose = (inv) => {
    if (!inv.customer_email) {
      toast.error("No customer email on file.");
      return;
    }
    setComposeInv(inv);
  };

  const handleSent = (updatedInv) => {
    setInvoices((list) => list.map((x) => (x.id === updatedInv.id ? updatedInv : x)));
  };

  // Preset form
  const [presetDesc, setPresetDesc] = useState("");
  const [presetAmount, setPresetAmount] = useState("");
  const [savingPreset, setSavingPreset] = useState(false);

  // Verify session
  useEffect(() => {
    if (authState === "authed") return;
    (async () => {
      try {
        const res = await axios.get(`${API}/auth/me`, {
          withCredentials: true,
          timeout: 10000,
        });
        setUser(res.data);
        setAuthState("authed");
      } catch (err) {
        if (err?.response?.status === 403) {
          setAuthError(
            err?.response?.data?.detail ||
              "Your account is not authorised to access this page."
          );
          setAuthState("forbidden");
          return;
        }
        setAuthState("unauthed");
        navigate("/admin/login", { replace: true });
      }
    })();
  }, [authState, navigate]);

  // Load invoices + presets
  useEffect(() => {
    if (authState !== "authed") return;
    (async () => {
      setLoading(true);
      try {
        const [invRes, presetRes] = await Promise.all([
          axios.get(`${API}/invoices`, {
            withCredentials: true,
            timeout: 15000,
          }),
          axios.get(`${API}/invoice-presets`, {
            withCredentials: true,
            timeout: 15000,
          }),
        ]);
        setInvoices(invRes.data);
        setPresets(presetRes.data);
      } catch (err) {
        console.error("Load failed:", err);
        toast.error("Couldn't load invoices. Please refresh.");
      } finally {
        setLoading(false);
      }
    })();
  }, [authState]);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (err) {
      console.warn("Logout failed:", err);
    }
    toast.success("Signed out");
    navigate("/admin/login", { replace: true });
  };

  // Stats: revenue this month (paid invoices issued this month) + outstanding
  const stats = useMemo(() => {
    const monthStart = startOfMonth();
    let thisMonthRevenue = 0;
    let outstanding = 0;
    let paidCount = 0;
    for (const inv of invoices) {
      if (inv.status === "paid") {
        paidCount++;
        const issued = new Date(inv.issued_date);
        if (issued >= monthStart) thisMonthRevenue += Number(inv.total || 0);
      } else {
        outstanding += Number(inv.total || 0);
      }
    }
    return {
      total: invoices.length,
      paid: paidCount,
      thisMonthRevenue,
      outstanding,
    };
  }, [invoices]);

  const filtered = useMemo(() => {
    if (filter === "all") return invoices;
    if (filter === "paid") return invoices.filter((i) => i.status === "paid");
    return invoices.filter((i) => i.status !== "paid");
  }, [invoices, filter]);

  const downloadPdf = async (inv) => {
    setBusyId(inv.id);
    try {
      const res = await axios.get(`${API}/invoices/${inv.id}/pdf`, {
        withCredentials: true,
        responseType: "blob",
        timeout: 20000,
      });
      const url = window.URL.createObjectURL(
        new Blob([res.data], { type: "application/pdf" })
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = `${inv.invoice_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("PDF failed:", err);
      toast.error("Couldn't download PDF.");
    } finally {
      setBusyId(null);
    }
  };

  const markPaid = async (inv) => {
    setBusyId(inv.id);
    try {
      const res = await axios.patch(
        `${API}/invoices/${inv.id}`,
        { status: "paid" },
        { withCredentials: true, timeout: 10000 }
      );
      setInvoices((list) => list.map((x) => (x.id === inv.id ? res.data : x)));
      toast.success("Marked as paid");
    } catch (err) {
      console.error("Mark paid failed:", err);
      toast.error("Couldn't update invoice.");
    } finally {
      setBusyId(null);
    }
  };

  const savePreset = async (e) => {
    e.preventDefault();
    const desc = presetDesc.trim();
    const amount = parseFloat(presetAmount);
    if (!desc) {
      toast.error("Give the preset a name.");
      return;
    }
    if (!Number.isFinite(amount) || amount === 0) {
      toast.error("Enter a non-zero amount (negative for discounts).");
      return;
    }
    setSavingPreset(true);
    try {
      const res = await axios.post(
        `${API}/invoice-presets`,
        { description: desc, amount },
        { withCredentials: true, timeout: 10000 }
      );
      setPresets((list) => [...list, res.data]);
      setPresetDesc("");
      setPresetAmount("");
      toast.success("Preset saved");
    } catch (err) {
      console.error("Save preset failed:", err);
      toast.error("Couldn't save preset.");
    } finally {
      setSavingPreset(false);
    }
  };

  const deletePreset = async (preset) => {
    if (!window.confirm(`Delete preset "${preset.description}"?`)) return;
    try {
      await axios.delete(`${API}/invoice-presets/${preset.id}`, {
        withCredentials: true,
        timeout: 10000,
      });
      setPresets((list) => list.filter((p) => p.id !== preset.id));
      toast.success("Preset removed");
    } catch (err) {
      console.error("Delete preset failed:", err);
      toast.error("Couldn't delete preset.");
    }
  };

  // ---------- Auth loading / forbidden states ----------
  if (authState === "checking") {
    return (
      <main className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-slate-500" />
      </main>
    );
  }
  if (authState === "forbidden") {
    return (
      <main className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <h1 className="text-2xl font-bold text-slate-900">Access denied</h1>
          <p className="mt-2 text-slate-600">{authError}</p>
          <button
            onClick={handleLogout}
            className="mt-6 inline-flex items-center justify-center h-11 px-6 rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-semibold"
          >
            Sign out and try a different account
          </button>
        </div>
      </main>
    );
  }

  return (
    <main
      data-testid="admin-invoices-page"
      className="min-h-screen bg-slate-50"
    >
      {/* Top bar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="text-2xl font-bold tracking-tight text-slate-900 hover:text-[#1E56B8]"
            >
              DazTec <span className="text-[#1E56B8]">Admin</span>
            </Link>
            <nav className="hidden sm:flex items-center gap-1 ml-2">
              <Link
                to="/admin"
                data-testid="admin-nav-enquiries"
                className="h-8 px-3 rounded-md text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-50"
              >
                Enquiries
              </Link>
              <Link
                to="/admin/invoices"
                data-testid="admin-nav-invoices"
                className="h-8 px-3 rounded-md text-sm font-medium bg-slate-100 text-slate-900 inline-flex items-center gap-1"
              >
                <FileText className="w-3.5 h-3.5" />
                Invoices
              </Link>
            </nav>
          </div>
          <div className="flex items-center gap-4">
            {user && (
              <div className="hidden sm:flex items-center gap-3 text-sm">
                {user.picture ? (
                  <img
                    src={user.picture}
                    alt={user.name}
                    className="w-8 h-8 rounded-full border border-slate-200"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-[#1E56B8] text-white flex items-center justify-center font-semibold">
                    {(user.name || user.email || "?").charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="leading-tight">
                  <div className="font-medium text-slate-900">{user.name}</div>
                  <div className="text-slate-500 text-xs">{user.email}</div>
                </div>
              </div>
            )}
            <button
              onClick={handleLogout}
              className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-100 text-sm font-medium"
            >
              <LogOut className="w-4 h-4" />
              Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            testId="stat-month-revenue"
            icon={DollarSign}
            label="This month (paid)"
            value={fmtMoney(stats.thisMonthRevenue)}
            tone="emerald"
          />
          <StatCard
            testId="stat-outstanding"
            icon={Clock}
            label="Outstanding"
            value={fmtMoney(stats.outstanding)}
            tone="amber"
          />
          <StatCard
            testId="stat-total-invoices"
            icon={FileText}
            label="Total invoices"
            value={stats.total}
            tone="slate"
          />
          <StatCard
            testId="stat-paid-invoices"
            icon={CheckCircle2}
            label="Paid invoices"
            value={stats.paid}
            tone="blue"
          />
        </div>

        {/* Filter */}
        <div className="flex items-center gap-2 mb-4" data-testid="invoice-filters">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              data-testid={`invoice-filter-${f.key}`}
              className={`h-9 px-4 rounded-lg text-sm font-medium border ${
                filter === f.key
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
              }`}
            >
              {f.label}
            </button>
          ))}
          <span className="ml-2 text-sm text-slate-500">
            {filtered.length} showing
          </span>
        </div>

        {/* List */}
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
              Loading invoices…
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center">
              <FileText className="w-8 h-8 text-slate-300 mx-auto" />
              <p className="mt-2 text-slate-500 text-sm">
                No invoices {filter !== "all" ? `in "${filter}"` : "yet"}.
              </p>
              <Link
                to="/admin"
                className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-[#1E56B8] hover:underline"
              >
                Go to Enquiries to create one
                <ExternalLink className="w-3.5 h-3.5" />
              </Link>
            </div>
          ) : (
            <ul data-testid="invoices-all-list" className="divide-y divide-slate-100">
              {filtered.map((inv) => {
                const meta = STATUS_META[inv.status] || STATUS_META.draft;
                const busy = busyId === inv.id;
                return (
                  <li
                    key={inv.id}
                    data-testid={`all-invoice-row-${inv.id}`}
                    className="px-5 py-4 hover:bg-slate-50/60"
                  >
                    <div className="flex flex-wrap gap-4 items-center justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-slate-900">
                          {inv.invoice_number}
                        </span>
                        <span
                          className={`inline-flex text-[11px] font-semibold px-2 py-0.5 rounded-full ${meta.className}`}
                        >
                          {meta.label}
                        </span>
                        <span className="text-slate-500 text-xs">
                          {fmtDate(inv.issued_date)}
                        </span>
                      </div>
                      <div className="mt-1 text-sm text-slate-700">
                        <span className="font-medium">{inv.customer_name || "—"}</span>
                        <span className="text-slate-400"> · </span>
                        <span className="text-slate-500">{inv.service_description}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div
                        data-testid={`all-invoice-total-${inv.id}`}
                        className="text-lg font-bold text-slate-900"
                      >
                        {fmtMoney(inv.total)}
                      </div>
                      {(inv.extra_lines?.length || 0) > 0 && (
                        <div className="text-[10px] uppercase tracking-wider text-slate-400">
                          incl. {inv.extra_lines.length} extra{inv.extra_lines.length === 1 ? "" : "s"}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      <button
                        onClick={() => downloadPdf(inv)}
                        disabled={busy}
                        data-testid={`all-invoice-download-${inv.id}`}
                        className="inline-flex items-center gap-1 h-8 px-3 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-100 text-xs font-medium disabled:opacity-60"
                      >
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                        PDF
                      </button>
                      <button
                        onClick={() => openCompose(inv)}
                        disabled={busy || !inv.customer_email}
                        title={!inv.customer_email ? "No customer email on file" : "Compose & send email"}
                        data-testid={`all-invoice-send-${inv.id}`}
                        className="inline-flex items-center gap-1 h-8 px-3 rounded-lg bg-[#1E56B8] hover:bg-[#2E6BD9] text-white text-xs font-semibold disabled:opacity-40"
                      >
                        <Send className="w-3.5 h-3.5" />
                        Send
                      </button>
                      <button
                        onClick={() =>
                          setExpandedEmailsFor((cur) => (cur === inv.id ? null : inv.id))
                        }
                        data-testid={`all-invoice-emails-toggle-${inv.id}`}
                        className="inline-flex items-center gap-1 h-8 px-3 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-100 text-xs font-medium"
                      >
                        <Inbox className="w-3.5 h-3.5" />
                        {expandedEmailsFor === inv.id ? "Hide" : "Sent emails"}
                      </button>
                      {inv.status !== "paid" && (
                        <button
                          onClick={() => markPaid(inv)}
                          disabled={busy}
                          data-testid={`all-invoice-mark-paid-${inv.id}`}
                          className="inline-flex items-center gap-1 h-8 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold disabled:opacity-60"
                        >
                          <DollarSign className="w-3.5 h-3.5" />
                          Mark paid
                        </button>
                      )}
                    </div>
                    </div>
                    {expandedEmailsFor === inv.id && (
                      <SentEmailsList invoiceId={inv.id} />
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Presets manager */}
        <section
          data-testid="presets-manager"
          className="mt-10 bg-white border border-slate-200 rounded-2xl p-6"
        >
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-5 h-5 text-[#1E56B8]" />
            <h2 className="text-lg font-bold text-slate-900">Reusable extras (presets)</h2>
          </div>
          <p className="text-sm text-slate-500 mb-5">
            Save the extras you charge most often (callout fees, cables, discounts) and add them to any invoice with a single tap.
          </p>

          <form
            onSubmit={savePreset}
            data-testid="preset-form"
            className="grid grid-cols-1 sm:grid-cols-[1fr,140px,auto] gap-2 mb-5"
          >
            <input
              type="text"
              value={presetDesc}
              onChange={(e) => setPresetDesc(e.target.value)}
              placeholder="e.g. Callout fee, USB-C cable, Loyalty discount"
              data-testid="preset-desc-input"
              className="h-10 px-3 rounded-lg border border-slate-300 bg-white text-sm text-slate-900 focus:border-[#1E56B8] focus:ring-0 outline-none"
              maxLength={120}
            />
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400 pointer-events-none">$</span>
              <input
                type="number"
                step="0.01"
                value={presetAmount}
                onChange={(e) => setPresetAmount(e.target.value)}
                placeholder="e.g. 25 or -5"
                data-testid="preset-amount-input"
                className="w-full h-10 pl-7 pr-3 rounded-lg border border-slate-300 bg-white text-sm text-slate-900 focus:border-[#1E56B8] focus:ring-0 outline-none"
              />
            </div>
            <button
              type="submit"
              disabled={savingPreset}
              data-testid="preset-save-btn"
              className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-[#1E56B8] hover:bg-[#2E6BD9] text-white text-sm font-semibold disabled:opacity-60 justify-center"
            >
              {savingPreset ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Save preset
            </button>
          </form>

          {presets.length === 0 ? (
            <p className="text-sm text-slate-400 italic">
              No presets yet. Add your first one above.
            </p>
          ) : (
            <ul
              data-testid="presets-list"
              className="divide-y divide-slate-100 border border-slate-200 rounded-xl"
            >
              {presets.map((p) => {
                const negative = Number(p.amount) < 0;
                return (
                  <li
                    key={p.id}
                    data-testid={`preset-row-${p.id}`}
                    className="px-4 py-3 flex items-center justify-between gap-4"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-slate-900 truncate">
                        {p.description}
                      </div>
                      <div
                        className={`text-xs font-semibold ${
                          negative ? "text-emerald-700" : "text-slate-600"
                        }`}
                      >
                        {negative ? "−" : "+"}${Math.abs(Number(p.amount)).toFixed(2)}
                      </div>
                    </div>
                    <button
                      onClick={() => deletePreset(p)}
                      data-testid={`preset-delete-${p.id}`}
                      className="h-8 w-8 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 flex items-center justify-center"
                      aria-label={`Delete preset ${p.description}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>

      <ComposeEmailModal
        inv={composeInv}
        onClose={() => setComposeInv(null)}
        onSent={(updated) => {
          handleSent(updated);
          setComposeInv(null);
        }}
      />
    </main>
  );
}

// ---------- Sent-emails list under each invoice ----------
function SentEmailsList({ invoiceId }) {
  const [emails, setEmails] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    setError(null);
    try {
      const res = await axios.get(`${API}/invoices/${invoiceId}/emails`, {
        withCredentials: true,
        timeout: 10000,
      });
      setEmails(res.data);
    } catch (e) {
      console.error("Load emails failed:", e);
      setError("Couldn't load sent emails.");
    }
  };

  useEffect(() => {
    load();
  }, [invoiceId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      data-testid={`sent-emails-${invoiceId}`}
      className="mt-3 border-t border-dashed border-slate-200 pt-3"
    >
      <div className="flex items-center gap-2 mb-2">
        <Mail className="w-3.5 h-3.5 text-slate-500" />
        <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">
          Sent emails
        </span>
        <button
          onClick={load}
          className="ml-auto text-slate-400 hover:text-slate-700"
          aria-label="Refresh"
        >
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>
      {error ? (
        <p className="text-sm text-rose-600">{error}</p>
      ) : emails === null ? (
        <div className="text-sm text-slate-500 flex items-center gap-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          Loading…
        </div>
      ) : emails.length === 0 ? (
        <p className="text-sm text-slate-500">Nothing sent yet.</p>
      ) : (
        <ul className="space-y-2">
          {emails.map((e) => (
            <SentEmailItem key={e.id} email={e} />
          ))}
        </ul>
      )}
    </div>
  );
}

function SentEmailItem({ email }) {
  const [open, setOpen] = useState(false);
  const dt = new Date(email.sent_at);
  return (
    <li
      data-testid={`sent-email-item-${email.id}`}
      className="bg-slate-50 border border-slate-200 rounded-lg"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex flex-wrap items-center gap-3 justify-between px-3 py-2 text-left"
      >
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-slate-900 truncate">
            {email.subject}
          </div>
          <div className="text-xs text-slate-500 truncate">
            To {email.to} · {dt.toLocaleString()}
          </div>
        </div>
        <span className="inline-flex items-center gap-1 text-[11px] font-medium text-[#1E56B8]">
          <Eye className="w-3.5 h-3.5" />
          {open ? "Hide" : "View"}
        </span>
      </button>
      {open && (
        <div className="border-t border-slate-200 bg-white p-3 rounded-b-lg">
          {email.attachment_filename && (
            <div className="mb-2 inline-flex items-center gap-1 text-[11px] text-slate-500 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5">
              <FileText className="w-3 h-3" />
              {email.attachment_filename}
            </div>
          )}
          <iframe
            title={`Email ${email.id}`}
            className="w-full min-h-[240px] bg-white rounded-md"
            sandbox=""
            srcDoc={email.html_body}
          />
        </div>
      )}
    </li>
  );
}

// ---------- Compose Email modal ----------
// (Moved to /components/admin/ComposeEmailModal.jsx and imported at the top.)
